import { navigate } from "@reach/router";
import UTMPurser from "../purser/index";

const hiddenClass = "js-hidden";

export const formValidation = {
    // Initializes other methods which establish necessary variables, listeners and a default form state.
    init(allValidationMessages) {
        // Establish necessary variables
        formValidation.setupVars(allValidationMessages);

        // Establish required listeners
        formValidation.setupListeners();

        // Disable submit button by default
        formValidation.disableForm();
    },

    // Establishes necessary variables from the available DOM elements.
    setupVars(allValidationMessages) {
        this.validatonMessages = allValidationMessages;
        this.firstNameInput =
            document.querySelector("[id^=FirstName]") ||
            document.querySelector("[id^=firstname]") ||
            document.querySelector("[id^=firstName]");
        this.lastNameInput =
            document.querySelector("[id^=LastName]") ||
            document.querySelector("[id^=lastname]") ||
            document.querySelector("[id^=lastName]");
        this.emailInput =
            document.querySelector("[id^=Email]") ||
            document.querySelector("[id^=email]") ||
            document.querySelector("[type=email]");
        this.phoneNumberInput = document.querySelector("[id^=Phone]") || document.querySelector("[id^=phone]");
        this.submitButton = document.querySelector('[id*="mktoForm"] button[type="submit"]');
        this.allRequiredInputs = document.querySelectorAll('.mktoField.mktoRequired:not([type="hidden"])');
    },

    // Establishes event listeners for the "required" form elements.
    setupListeners() {
        for (let i = 0; i < this.allRequiredInputs.length; i++) {
            const inputEl = this.allRequiredInputs[i];
            // Whenever a field is changed, validate the form
            inputEl.addEventListener("change", formValidation.validate);
            inputEl.addEventListener("keyup", formValidation.validate);
        }
    },

    /**
     * Changes classes and places error message into DOM.
     * @param {object} inputEl The input element which has an erroneous value.
     * @param {string} message The error message to display.
     */
    showError(inputEl, message) {
        inputEl.classList.add("is-invalid");
        let errorEl = document.getElementById(inputEl.id + "-error");
        let errorMarkup = `<div id="${inputEl.id}-error" class="form-error mktoErrorMsg">${message}</div>`;

        if (!errorEl) {
            inputEl.insertAdjacentHTML("afterend", errorMarkup);
        }
    },

    /**
     * Removes the error markup from the DOM
     * @param {object} inputEl The input element from which the error should be removed.
     */
    hideError(inputEl) {
        inputEl.classList.remove("is-invalid");

        let errorEl = document.getElementById(inputEl.id + "-error");
        if (errorEl) errorEl.parentNode.removeChild(errorEl);
    },

    /**
     * Tests an input element's value against a particular RegEx
     * @param {object} inputEl The input element to test.
     * @param {string} errorMessage The error message to display if the test fails.
     * @param {string} testType The type of test – 'name' or 'phone'.
     */
    testField(inputEl, errorMessage, testType = "name") {
        const nameExp = /^[^0-9.,_!¡?÷?¿/\\+=@#$%ˆ&*(){}|~<>;:[\]]*$/;
        const phoneExp = /^[0-9- ()+]+$/;
        const emailExp = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        let result = false;

        if (inputEl === null || inputEl === undefined) {
            // If the element isn't present, skip validation
            return true;
        }

        // If the field exists and has a value
        if (inputEl && inputEl.value) {
            // If testing for a name
            if (testType === "name") result = nameExp.test(inputEl.value);

            // If testing for a phone number
            if (testType === "phone" && inputEl.value.length >= 8 && inputEl.value.length <= 20)
                result = phoneExp.test(inputEl.value);

            // If testing for an email address
            // Will return before custom error message and will show Marketo's error message
            if (testType === "email") return emailExp.test(inputEl.value);

            // If the element doesn't pass the RegEx test
            if (result == false) {
                // Show the associated error message
                formValidation.showError(inputEl, errorMessage);

                // Ensure the field can be seen
                inputEl.parentElement.classList.remove("js-hidden");
            } else {
                // If the test result is good, hide the error
                formValidation.hideError(inputEl);
            }
        }

        return result;
    },

    // Reviews all present required fields (.mktoRequired)
    bulkReview() {
        let formComplete = true;
        for (let i = 0; i < this.allRequiredInputs.length; i++) {
            const reqEl = this.allRequiredInputs[i];

            // The types of inputs which have a "value"
            let inputTypesWithValues = ["text", "select-one", "select-multiple"];

            if (
                (inputTypesWithValues.indexOf(reqEl.type) !== -1 && reqEl.value == "") ||
                (reqEl.type === "checkbox" && reqEl.checked === false)
            ) {
                formComplete = false;
            }
        }
        return formComplete;
    },

    // Disables the submit button
    disableForm() {
        // Some times this is null
        if (this.submitButton !== null) {
            this.submitButton.setAttribute("disabled", "disabled");
        }
    },

    /**
     * Primary Marketo form validation function
     * Sets error messages, performs custom validation of First Name, Last Name and Phone,
     * then changes the state of the submit button depending on the validation of the form's fields.
     */
    validate() {
        // Setting default English error messages
        let firstNameError = "First Name must not include<br>symbols or numbers.";
        let lastNameError = "Last Name must not include<br>symbols or numbers.";
        let phoneError = "Phone number is invalid. Must be 8 to 20 digits.";

        // Acquiring localised error strings, if available
        if (formValidation.validatonMessages.firstname_error)
            firstNameError = formValidation.validatonMessages.firstname_error;
        if (formValidation.validatonMessages.lastname_error)
            lastNameError = formValidation.validatonMessages.lastname_error;
        if (formValidation.validatonMessages.phone_error) phoneError = formValidation.validatonMessages.phone_error;
        if (formValidation.validatonMessages.email_error) emailError = formValidation.validatonMessages.email_error;

        // Begin "custom" validation of First Name, Last Name and Phone
        let validFirstName = formValidation.testField(formValidation.firstNameInput, firstNameError);
        let validLastName = formValidation.testField(formValidation.lastNameInput, lastNameError);
        let validEmail = formValidation.testField(formValidation.emailInput, null, "email");
        let validPhoneNumber = formValidation.testField(formValidation.phoneNumberInput, phoneError, "phone");

        // Review the required fields in bulk
        let bulkState = formValidation.bulkReview();

        // If the required fields are valid
        if (
            validFirstName === true &&
            validLastName === true &&
            validEmail === true &&
            validPhoneNumber === true &&
            bulkState === true
        ) {
            // Enable the submit button
            formValidation.submitButton.removeAttribute("disabled");
        } else {
            // Disable the submit button
            formValidation.disableForm();
        }
    },
};

export const destyleMktoForm = (mktoForm, options) => {
    const formEl = mktoForm.getFormElem()[0];
    const arrayFrom = Function.prototype.call.bind(Array.prototype.slice);
    options = options || {};

    // remove element styles from <form> and children
    if (!options.keepInline) {
        const styledEls = arrayFrom(formEl.querySelectorAll("[style]")).concat(formEl);
        styledEls.forEach(function(el) {
            el.removeAttribute("style");
        });

        // Remove all <style> element contents from the form
        const styleEls = formEl.querySelectorAll("style");
        styleEls.forEach(function(el) {
            el.parentNode.removeChild(el);
        });
    }
    // disable remote stylesheets and local styles
    if (!options.keepSheets) {
        const styleSheets = arrayFrom(document.styleSheets);
        styleSheets.forEach(function(ss) {
            if (
                // eslint-disable-next-line no-undef
                [mktoForms2BaseStyle, mktoForms2ThemeStyle].indexOf(ss.ownerNode) !== -1 ||
                formEl.contains(ss.ownerNode)
            ) {
                ss.disabled = true;
            }
        });
    }

    if (!options.moreStyles) {
        formEl.setAttribute("data-styles-ready", "true");
    }
};

/**
 * This function looks for the checkbox text in the form from the qlik marketo instance
 * and then copies it and puts it where the checkbox text should be in our talend/stitch marketo instance.
 * This allows us to re-use the existing CSS written for out talend/stitch forms to style it properly with
 * very little effort.
 */
export const fetchCheckboxText = () => {
    // iterate through checkboxes in the form (there should really only be 1 for our forms...)
    let checkWrappers = document.getElementsByClassName("mktoCheckboxList");
    for (let i = 0; i < checkWrappers.length; i++) {
        const checkEl = checkWrappers[i];

        // grab the checkbox text node
        let labelTextNode = checkEl.parentElement.parentElement.parentElement.querySelector(
            ".mktoFormCol > .mktoFieldWrap > .mktoHtmlText > span"
        );

        // get a handle on where we want to copy it to (destination)
        let labelEl = checkEl.parentElement.querySelector("label.mktoLabel");

        // this cleans up the checkbox text destination, for when this function is called multiple times
        // usually due to changing the country dropdown
        labelEl.querySelector("span") && labelEl.querySelector("span").remove();

        // if both the text and the destination are present
        if (labelTextNode && labelEl) {
            // clone the text node
            const labelText = labelTextNode.cloneNode(true);

            // append it into the destination, since there is usually already a asterisk element in there.
            labelEl.appendChild(labelText);

            // in case the cloned node appended into 'labelEl' contains a 'hidden' class, remove it
            labelEl.querySelector("span").classList.contains("hidden") &&
                labelEl.querySelector("span").classList.remove("hidden");

            // hide the original text node, it gets overwritten on country update, so can't remove it
            labelTextNode.classList.add("hidden");
        }
    }
};

export const applyCheckboxClasses = () => {
    // Adding class to field wrapper elements when checkbox is present
    let checkWrappers = document.getElementsByClassName("mktoCheckboxList");
    for (let i = 0; i < checkWrappers.length; i++) {
        const checkEl = checkWrappers[i];
        checkEl.parentNode.classList.add("js-has-checkbox");
    }
};

/**
 * Form Experiment Fill
 * When the Optimizely object is available, this function places Optimizely-specific
 * info into specific hidden Marketo fields.
 */

export const formExperimentFill = () => {
    // If an Optimizely object exists and is fully featured
    if (window.optimizely && window.optimizely.get) {
        // Acquire Optimizely 'state' object
        let optState = window.optimizely.get("state");

        // Acquire form fields to be populated
        let mktoExpField = document.getElementsByName("Optimizely_Experiment_Name__c")[0];
        let mktoVarField = document.getElementsByName("Optimizely_Variation_Name__c")[0];

        // Ensure Marketo fields are present
        if (mktoExpField && mktoVarField) {
            // Acquire the current experiment states
            let optExperiments = optState.getExperimentStates();

            let experimentName = "";
            let variationName = "";
            let i = 0;

            // Loop through present experiments
            for (let j in optExperiments) {
                const exp = optExperiments[j];

                if (i > 0) {
                    experimentName += " | ";
                    variationName += " | ";
                }

                // Experiment Name concatenation
                // The 'null' string value is intentional for sorting and filtering in Tableau
                exp["experimentName"] ? (experimentName += exp["experimentName"]) : (experimentName += "null");

                // Variation concatenation
                // The 'null' string value is intentional for sorting and filtering in Tableau
                exp["variation"] ? (variationName += exp["variation"]["name"]) : (variationName += "null");
                i = i + 1;
            }

            // Set Marketo fields with formatted values
            mktoExpField.value = experimentName;
            mktoVarField.value = variationName;
        }
    }
};

export const addPurserValuesToMarketoForms = (form) => {
    // Get the values from the Purser
    const purserValues = UTMPurser.get();

    // Instantiate a variable for use later
    let latestValues;

    // If the visitor has been to the site before, pull the values from the latest visit
    if (Object.prototype.hasOwnProperty.call(purserValues, "visits")) {
        latestValues = purserValues["visits"][purserValues["visits"].length - 1];
    } else {
        // Else just pull the values directly from Purser
        latestValues = purserValues;
    }

    // Define the values to be used
    const utmValues = Object.keys(latestValues).reduce((newData, propertyName) => {
        newData[propertyName] = latestValues[propertyName];
        return newData;
    }, {});

    // Mapping of UTM / Purser values to Marketo field ids
    const mapping = {
        utm_source: "utm_source__c",
        utm_medium: "utm_medium__c",
        utm_campaign: "utm_campaign__c",
        utm_content: "utm_content__c",
        utm_term: "utm_term__c",
        utm_team: "UTM_Team__c",
        utm_subtype: "UTM_SubType__c",
        utm_partner_id: "UTM_Partner_ID__c",
        utm_mpt_id: "UTM_MPT_ID__c",
        ppc_id: "PPC_ID__c",
        gclid: "GCLID__c",
    };

    // Reduce the mapped values to only those that are present in the mapping object, and match with the IDs
    const mappedValues = Object.keys(mapping).reduce((newData, key) => {
        if (Object.prototype.hasOwnProperty.call(utmValues, key)) {
            newData[mapping[key]] = utmValues[key];
        }
        return newData;
    }, {});

    // Set the form fields to corresponding values from mappedValues
    form.setValues(mappedValues);

    // Get the form element
    const formEl = form.getFormElem()[0];

    // If there is a field named "Entry_URL__c", set it to the landing page
    if (formEl.Entry_URL__c !== undefined) {
        form.setValues({ Entry_URL__c: latestValues.landing_page });
    } else {
        form.addHiddenFields({ Entry_URL__c: latestValues.landing_page });
    }

    // If there is a field named "sourceURL", set it to the referrer
    if (formEl.sourceURL !== undefined) {
        form.setValues({ sourceURL: latestValues.referrer });
    } else {
        form.addHiddenFields({ sourceURL: latestValues.referrer });
    }
};

/**
 * ZoomInfo Integration
 * Object with methods primarily used to initialize ZoomInfo with Marketo forms.
 */
export const zoomInfoIntegration = {
    // Initilizes ZoomInfo configuration options and sets a fallback timer.
    init(form) {
        if (window._zi && window._zi.formId) {
            // Create <script> tag for integrating ZoomInfo snippet
            var zi = document.createElement("script");
            zi.type = "text/javascript";
            zi.async = true;
            zi.src = "//ws-assets.zoominfo.com/formcomplete.js";
            var zis = document.getElementsByTagName("script")[0];
            zis.parentNode.insertBefore(zi, zis);
        } else {
            // If no ZoomInfo object, show all fields and return
            zoomInfoIntegration.displayAllFields(form.getFormElem()[0]);
            return;
        }

        // Assign callback method to ZoomInfo object
        window._zi.callbacks = { onMatch: zoomInfoIntegration.onMatch };

        const fallbackTimeout = window._zi.fallbackTimeout || 2000;

        // Fallback for if/when ZoomInfo can't connect or fails
        setTimeout(() => {
            let mainForm = MktoForms2.allForms()[0];
            let formEl = mainForm.getFormElem()[0];

            // If the data-zi-mapped-form data attribute is missing
            if (formEl.hasAttribute("data-zi-mapped-form") === false) {
                // ZoomInfo has not loaded
                formValidation.zoomLoaded = false;
                zoomInfoIntegration.displayAllFields(formEl);
            }
        }, fallbackTimeout);

        zoomInfoIntegration.showForm(form);
    },

    // Display ALL of the form fields, carte-blanche
    displayAllFields(formEl) {
        // Add class for revealing all fields
        formEl.classList.add("js-show-all-fields");

        // Remove the hiddenClass from the associated fields, revealing them to the user
        const fieldWraps = document.querySelectorAll("form.js-show-all-fields .mktoFieldWrap");
        for (let i = 0; i < fieldWraps.length; i++) {
            const wrapEl = fieldWraps[i];
            wrapEl.classList.remove(hiddenClass);
        }

        formEl.classList.add("js-form-loaded");
    },

    // Hides all 'text' and 'select' form fields, then displays the form
    showForm(form) {
        const hiddenFieldTypes = ["text", "select-one", "tel"];
        const mktoFields = document.getElementsByClassName("mktoField");

        for (let i = 0; i < mktoFields.length; i = i + 1) {
            const fieldEl = mktoFields[i];

            // If the field's type is in the hiddenFieldTypes array, hide it
            if (hiddenFieldTypes.includes(fieldEl.type)) {
                fieldEl.parentElement.classList.add(hiddenClass);

                // If the field is 'country', and showCountry flag is set to 'true'
                if (window._zi.showCountry && fieldEl.name.toString().toLowerCase() === "country") {
                    // Show the country field
                    fieldEl.parentElement.classList.remove(hiddenClass);
                }
            }
        }

        // Show form once it's loaded and the fields are appropriately displayed
        form.getFormElem()[0].classList.add("js-form-loaded");
    },

    // Hides or shows fields based on when a ZoomInfo match is found
    onMatch(matchResult) {
        const mktoFields = document.getElementsByClassName("mktoField");

        // Loop through all Marketo fields
        for (let i = 0; i < mktoFields.length; i = i + 1) {
            const fieldEl = mktoFields[i];

            // 1) If the field is not part of the matched set,
            // 2) or it has no selected value (ie: a <select> field)
            // 3) or it's the email field
            if (!matchResult[fieldEl.name] || fieldEl.value == "" || fieldEl.type == "email") {
                // Show the field
                fieldEl.parentElement.classList.remove(hiddenClass);
            } else {
                // Hide the field
                fieldEl.parentElement.classList.add(hiddenClass);
            }
        }

        // Run form validation against the currently available data
        formValidation.validate();
    },
};

/**
 * Form onSuccess Callbacks
 */

export const trustAssessor = (form) => {
    form.onSuccess((values, followUpUrl) => {
        const email = values.Email;
        const ENDPOINT = "https://assessor.trustscore.talend.com/user/register";
        const FRONTEND = "https://assessor.trustscore.talend.com/saveuser/";
        const STORAGE_KEY = "@talend-trust-assessor";

        const onRegisterSuccess = (response) => {
            if (oReq.status === 200) {
                localStorage.setItem(STORAGE_KEY, JSON.parse(oReq.responseText).unique_id);
                location.href = FRONTEND + JSON.parse(oReq.responseText).unique_id;
            }
        };

        let oReq = new XMLHttpRequest();
        oReq.onload = onRegisterSuccess;
        oReq.open("POST", ENDPOINT, true);
        oReq.send(JSON.stringify({ email: email, responses: values }));

        // Return false to prevent the submission handler continuing with its own processing
        return false;
    });
};

export const stitchSignUp = (form) => {
    form.onSuccess((values, followUpUrl) => {
        const vals = form.vals();
        location.href = "https://app.stitchdata.com/signup?email=" + vals.Email;
        return false;
    });
};

export const normalFormSubmission = (form, thankYouMessage, formConversionType) => {
    form.onSuccess((values, followUpUrl) => {
        const id = form.getId();

        // Add Tracking Param to URL
        // This will navigate to a new location and take
        // care of all the GA events and such
        navigate("?ty=" + formConversionType);

        // Hide the form
        form.getFormElem().hide();

        // Thank you message display needs some loves ...
        const formContainer = document.querySelector(".formHolder.form-id-" + id);

        // Remove all child nodes in the form container
        var child = formContainer.lastElementChild;
        while (child) {
            formContainer.removeChild(child);
            child = formContainer.lastElementChild;
        }

        // Add span tag with class='thank-you-message' with the thank you text
        if (document.querySelectorAll("span.thank-you-message").length === 0) {
            let thankYou = document.createElement("span");
            thankYou.classList.add("thank-you-message");
            thankYou.innerHTML = thankYouMessage;
            formContainer.appendChild(thankYou);

            // display promoBanners if they are hidden on the page
            document.querySelector(".formTYComponents") &&
                document.querySelector(".formTYComponents").classList.add("show");
        }

        return false;
    });
};
